import React, { useCallback, useEffect, useState } from "react";
import { MdClose } from "react-icons/md";
import { toast } from "react-toastify";
import Modal from "../../../../components/modal";
import Switch from "../../../../components/switch";
import { PessoaDetalhe, PessoaVinculoDetalhe } from "../../../../interfaces/Identidade";
import { Profile } from "../../../../interfaces/Profile";
import {
  adicionarVinculo,
  definirPerfisDoVinculo,
  definirPessoaInterna,
  desativarVinculo,
  getPessoa,
} from "../../../../services/identidadeApi";
import { mensagemDoServidor } from "../../../../utils/mensagemDoServidor";
import VinculoPerfis from "./VinculoPerfis";

export interface ClienteOpcao {
  id: number;
  nome: string;
}

interface Props {
  pessoaId: number;
  perfisAtivos: Profile[];
  clientesAtivos: ClienteOpcao[];
  onClose: () => void;
  /** Avisa a lista que algo mudou (chips de organização, Interna). */
  onChanged: () => void;
}

const SECAO = "text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400";

/** Painel lateral com o detalhe da pessoa: logins, colaboradores, vínculos e perfis. */
const PessoaPainel: React.FC<Props> = ({ pessoaId, perfisAtivos, clientesAtivos, onClose, onChanged }) => {
  const [pessoa, setPessoa] = useState<PessoaDetalhe | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);
  const [busy, setBusy] = useState(false);
  const [novoCliente, setNovoCliente] = useState("");

  const carregar = useCallback(async () => {
    try {
      setPessoa(await getPessoa(pessoaId));
      setErro(false);
    } catch {
      setErro(true);
    } finally {
      setLoading(false);
    }
  }, [pessoaId]);

  useEffect(() => {
    setLoading(true);
    carregar();
  }, [carregar]);

  /** Executa uma escrita, recarrega o detalhe e avisa a lista. */
  const executar = async (acao: () => Promise<void>, sucesso: string, falha: string) => {
    setBusy(true);
    try {
      await acao();
      toast.success(sucesso);
      await carregar();
      onChanged();
    } catch (e) {
      toast.error(mensagemDoServidor(e, falha));
    } finally {
      setBusy(false);
    }
  };

  const adicionar = async () => {
    if (!pessoa || !novoCliente) return;
    await executar(
      () => adicionarVinculo(pessoa.id, Number(novoCliente)),
      "Organização adicionada.",
      "Não foi possível adicionar a organização."
    );
    setNovoCliente("");
  };

  const alternarAtivo = (v: PessoaVinculoDetalhe) =>
    executar(
      () =>
        v.ativo
          ? desativarVinculo(pessoa!.id, v.clientId)
          : adicionarVinculo(pessoa!.id, v.clientId),
      v.ativo ? "Vínculo desativado." : "Vínculo reativado.",
      "Não foi possível alterar o vínculo."
    );

  const salvarPerfis = (v: PessoaVinculoDetalhe, ids: number[]) =>
    executar(
      () => definirPerfisDoVinculo(v.vinculoId, ids),
      "Perfis salvos.",
      "Não foi possível salvar os perfis."
    );

  const alternarInterna = () =>
    executar(
      () => definirPessoaInterna(pessoa!.id, !pessoa!.interna),
      "Equipe interna atualizada.",
      "Não foi possível alterar a equipe interna."
    );

  // Clientes sem vínculo com a pessoa (vínculo desativado se reativa pelo botão do próprio vínculo).
  const disponiveis = clientesAtivos.filter(
    (c) => !pessoa?.vinculos.some((v) => v.clientId === c.id)
  );

  return (
    <Modal onClose={onClose}>
      <aside
        aria-label="Detalhe da pessoa"
        onClick={(e) => e.stopPropagation()}
        className="ml-auto flex max-h-full w-full max-w-xl flex-col self-stretch overflow-hidden rounded-[20px] bg-white shadow-dv-lg dark:bg-navy-800"
      >
        <header className="flex items-start justify-between gap-3 border-b border-gray-100 p-5 dark:border-white/10">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-extrabold text-navy-700 dark:text-white">
              {pessoa?.nome ?? "Pessoa"}
            </h2>
            {pessoa && (
              <p className="truncate text-sm text-gray-600 dark:text-gray-300" title={pessoa.emailPrincipal}>
                {pessoa.emailPrincipal}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-dv-blue-600 dark:text-gray-400 dark:hover:bg-navy-700"
          >
            <MdClose className="h-5 w-5" aria-hidden="true" />
          </button>
        </header>

        <div className="flex-1 space-y-6 overflow-y-auto p-5">
          {loading && (
            <div className="space-y-3" aria-busy="true" aria-label="Carregando pessoa">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-16 animate-pulse rounded-xl bg-gray-100 dark:bg-white/5" />
              ))}
            </div>
          )}

          {!loading && erro && (
            <div
              role="alert"
              className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-900/20 dark:text-red-300"
            >
              Não foi possível carregar esta pessoa. Feche o painel e tente de novo.
            </div>
          )}

          {!loading && pessoa && (
            <>
              <section className="flex items-center justify-between gap-3 rounded-2xl border border-gray-100 p-4 dark:border-white/10">
                <div>
                  <label htmlFor="pessoa-interna" className="text-sm font-semibold text-navy-700 dark:text-white">
                    Equipe interna
                  </label>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Pessoas internas não são tratadas como clientes no portal.
                  </p>
                </div>
                <Switch
                  id="pessoa-interna"
                  checked={pessoa.interna}
                  disabled={busy}
                  onChange={alternarInterna}
                />
              </section>

              <section>
                <h3 className={SECAO}>Logins</h3>
                {pessoa.logins.length === 0 ? (
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Nenhum login ligado.</p>
                ) : (
                  <ul className="mt-2 space-y-1">
                    {pessoa.logins.map((l) => (
                      <li key={l.identityUserId} className="truncate text-sm text-gray-700 dark:text-gray-300">
                        {l.email ?? "(sem e-mail)"}
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section>
                <h3 className={SECAO}>Colaboradores</h3>
                {pessoa.colaboradores.length === 0 ? (
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Nenhum cadastro de colaborador.
                  </p>
                ) : (
                  <ul className="mt-2 space-y-1">
                    {pessoa.colaboradores.map((c) => (
                      <li
                        key={c.id}
                        className="flex items-center justify-between gap-2 text-sm text-gray-700 dark:text-gray-300"
                      >
                        <span className="truncate" title={c.email}>
                          {c.nome ?? c.email}
                        </span>
                        <span
                          className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                            c.ativo
                              ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                              : "bg-gray-100 text-gray-600 dark:bg-navy-700 dark:text-gray-300"
                          }`}
                        >
                          {c.ativo ? "Ativo" : "Inativo"}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section>
                <h3 className={SECAO}>Organizações e perfis</h3>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  O que cada perfil faz se define em Perfis de acesso.
                </p>
                {pessoa.vinculos.length === 0 ? (
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Sem organizações. Adicione uma abaixo para liberar acesso.
                  </p>
                ) : (
                  <ul className="mt-2 space-y-3">
                    {pessoa.vinculos.map((v) => (
                      <VinculoPerfis
                        key={v.vinculoId}
                        vinculo={v}
                        perfisAtivos={perfisAtivos}
                        busy={busy}
                        onToggleAtivo={alternarAtivo}
                        onSalvarPerfis={salvarPerfis}
                      />
                    ))}
                  </ul>
                )}

                <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-end">
                  <div className="min-w-0 flex-1">
                    <label
                      htmlFor="novo-vinculo"
                      className="text-sm font-semibold text-gray-700 dark:text-gray-300"
                    >
                      Adicionar organização
                    </label>
                    <select
                      id="novo-vinculo"
                      value={novoCliente}
                      onChange={(e) => setNovoCliente(e.target.value)}
                      disabled={busy}
                      className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                    >
                      <option value="">Selecione um cliente</option>
                      {disponiveis.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    disabled={busy || !novoCliente}
                    onClick={adicionar}
                    className="rounded-xl bg-ink-900 px-5 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
                  >
                    Adicionar
                  </button>
                </div>
              </section>
            </>
          )}
        </div>
      </aside>
    </Modal>
  );
};

export default PessoaPainel;
